import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import FeedbackBar from '../../feedbackBar/FeedbackBar';

// Fixed profit used only for the "How it works" illustration on this screen.
const EXAMPLE_WEEKLY_PROFIT = 5000;

class ManagerSetting extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      manager: { name: '', percentage: '', profitThreshold: '' },
      partners: [],
      feedbackBarOpen: false,
      feedbackMessage: '',
      feedbackStatus: '',
      isValid: false,
    };
  }

  componentDidMount() {
    const setting = this.props.managerSetting;
    if (setting) {
      this.setState(
        {
          manager: {
            name: setting.managerName || '',
            percentage: setting.managerPercentage ?? '',
            profitThreshold: setting.profitThreshold ?? '',
          },
          partners: (setting.partners || []).map((p) => ({
            name: p.name || '',
            percentage: p.percentage ?? '',
          })),
        },
        this.validateForm
      );
    }
  }

  handleManagerChange = (field, value) => {
    this.setState(
      (prevState) => ({
        manager: { ...prevState.manager, [field]: value },
      }),
      this.validateForm
    );
  };

  handlePartnerChange = (index, field, value) => {
    this.setState((prevState) => {
      const partners = [...prevState.partners];
      partners[index] = { ...partners[index], [field]: value };
      return { partners };
    }, this.validateForm);
  };

  addPartner = () => {
    this.setState(
      (prevState) => ({
        partners: [...prevState.partners, { name: '', percentage: '' }],
      }),
      this.validateForm
    );
  };

  removePartner = (index) => {
    this.setState(
      (prevState) => ({
        partners: prevState.partners.filter((_, i) => i !== index),
      }),
      this.validateForm
    );
  };

  validateForm = () => {
    const { manager, partners } = this.state;

    // Manager validation
    if (
      !manager.name ||
      manager.percentage === '' ||
      manager.percentage < 0 ||
      manager.percentage > 100 ||
      manager.profitThreshold === '' || // cannot be empty
      manager.profitThreshold == null || // handle null/undefined
      manager.profitThreshold < 0 // cannot be negative
    ) {
      this.setState({ isValid: false });
      return;
    }

    // Partner validation — each row needs a name and a 0–100 percentage.
    const partnersValid = partners.every(
      (p) =>
        String(p.name || '').trim() !== '' &&
        p.percentage !== '' &&
        p.percentage != null &&
        Number(p.percentage) >= 0 &&
        Number(p.percentage) <= 100
    );

    if (!partnersValid) {
      this.setState({ isValid: false });
      return;
    }

    this.setState({ isValid: true });
  };

  handleSave = () => {
    const partners = this.state.partners
      .map((p) => ({
        name: String(p.name || '').trim(),
        percentage: parseFloat(p.percentage) || 0,
      }))
      .filter((p) => p.name && p.percentage > 0);

    const data = {
      location:
        this.props.managerSetting?.locationId ||
        this.props.userInfo?.selectedLocationId,
      manager: { ...this.state.manager, partners },
    };

    const callback = (error, response) => {
      if (error) {
        this.openFeedbackBar('error', error);
      } else {
        this.openFeedbackBar('success', response);
      }
    };

    this.props.saveManagerSetting(data, callback);
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState({
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
    });
  };

  fmtMoney = (value) =>
    (Number(value) || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // Live worked example against a fixed $5,000 weekly profit.
  computeExample = () => {
    const { manager, partners } = this.state;
    const weeklyProfit = EXAMPLE_WEEKLY_PROFIT;
    const managerPct = parseFloat(manager.percentage) || 0;
    const profitThreshold = parseFloat(manager.profitThreshold) || 0;

    let managerShare = 0;
    if (weeklyProfit > profitThreshold) {
      managerShare = (weeklyProfit - profitThreshold) * (managerPct / 100);
    }

    const remaining = weeklyProfit - managerShare;

    const partnerLines = partners
      .filter(
        (p) =>
          String(p.name || '').trim() && (parseFloat(p.percentage) || 0) > 0
      )
      .map((p) => {
        const pct = parseFloat(p.percentage) || 0;
        return {
          name: p.name,
          percentage: pct,
          share: remaining * (pct / 100),
        };
      });

    const totalPartnerShare = partnerLines.reduce((sum, p) => sum + p.share, 0);
    const ownerProfit = remaining - totalPartnerShare;

    return {
      weeklyProfit,
      managerPct,
      managerShare,
      partnerLines,
      ownerProfit,
    };
  };

  render() {
    const { manager, partners, isValid } = this.state;
    const example = this.computeExample();

    return (
      <React.Fragment>
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        <Box
          sx={{
            p: { xs: 2, sm: 4 },
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <Paper
            sx={{
              p: { xs: 2, sm: 4 },
              borderRadius: 4,
              width: '100%',
              maxWidth: 700,
              textAlign: 'left',
              background: 'linear-gradient(145deg, #ffffff, #f9f9f9)',
              border: '0px solid rgba(0, 0, 0, 0.08)',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Manager Information */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.25,
                mb: 0.5,
              }}
            >
              <PersonOutlineIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Manager Details
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure the manager&apos;s weekly commission.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={7}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Manager Name"
                  value={manager.name}
                  onChange={(e) =>
                    this.handleManagerChange('name', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Manager %"
                  type="number"
                  value={manager.percentage}
                  onChange={(e) =>
                    this.handleManagerChange('percentage', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={5}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Profit Threshold"
                  type="number"
                  value={manager.profitThreshold}
                  onChange={(e) =>
                    this.handleManagerChange('profitThreshold', e.target.value)
                  }
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* Profit Distribution (Partners) */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.25,
                mb: 0.5,
              }}
            >
              <GroupsIcon sx={{ color: '#2e7d32' }} />
              <Typography variant="h6" fontWeight="bold">
                Profit Distribution (Partners)
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Partners who receive their share directly from the manager. Their
              percentage is automatically deducted from the profit remaining
              after the manager&apos;s commission, before the balance is
              delivered to the owner(s).
            </Typography>

            {partners.map((partner, index) => (
              <Grid
                container
                spacing={2}
                key={index}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Grid item xs={12} sm={5}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Partner Name"
                    value={partner.name}
                    onChange={(e) =>
                      this.handlePartnerChange(index, 'name', e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={8} sm={4}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Partner Share %"
                    type="number"
                    value={partner.percentage}
                    onChange={(e) =>
                      this.handlePartnerChange(
                        index,
                        'percentage',
                        e.target.value
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  sm={3}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                  }}
                >
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon />}
                    label="From manager"
                    color="success"
                    variant="outlined"
                    sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => this.removePartner(index)}
                    aria-label="remove partner"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              startIcon={<AddCircleOutlineIcon />}
              variant="outlined"
              color="success"
              onClick={this.addPartner}
              sx={{ mb: 3 }}
            >
              Add Partner
            </Button>

            {/* How it works (Example) */}
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 3 },
                mb: 3,
                borderRadius: 3,
                bgcolor: '#fafafa',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1.25,
                  mb: 1.5,
                }}
              >
                <CalculateOutlinedIcon sx={{ color: '#5e35b1' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  How it works (Example)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">Weekly Net Profit</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    ${this.fmtMoney(example.weeklyProfit)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">
                    Manager Commission ({example.managerPct || 0}%)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#6a1b9a', whiteSpace: 'nowrap' }}
                    fontWeight={600}
                  >
                    -${this.fmtMoney(example.managerShare)}
                  </Typography>
                </Box>

                {example.partnerLines.map((p, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                      Partner: {p.name} ({p.percentage}%)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#2e7d32', whiteSpace: 'nowrap' }}
                      fontWeight={600}
                    >
                      -${this.fmtMoney(p.share)}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 0.5 }} />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: '#e3f2fd',
                    p: 1.2,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    sx={{ color: '#1565c0' }}
                  >
                    Amount to Deliver to Owner(s)
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    sx={{ color: '#1565c0', whiteSpace: 'nowrap' }}
                  >
                    ${this.fmtMoney(example.ownerProfit)}
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  * Example only. Amounts will change based on actual profit.
                </Typography>
              </Box>
            </Paper>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'flex-start',
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                mt: 1,
              }}
            >
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={this.handleSave}
                disabled={!isValid}
                sx={{
                  px: { sm: 4 },
                  py: 1.2,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Save
              </Button>

              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={this.props.isBack}
                sx={{
                  px: { sm: 4 },
                  py: 1.2,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </React.Fragment>
    );
  }
}

export default ManagerSetting;
